import React, { useState } from 'react';
import { ShieldCheck, X, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { isEligibleForYarboCare, YARBOCARE_PLANS, YARBOCARE_DISCLAIMER, YarboCarePlanId } from '../data/yarboCare';
/**
 * Cart-page YarboCare upsell panel.
 * Renders one row per eligible item that doesn't yet have a protection plan.
 * Does not render at all if every eligible item already has protection
 * or if there are no eligible items.
 */
export function YarboCareUpsellPanel() {
  const {
    items,
    attachProtection
  } = useCart();
  const eligibleWithoutProtection = items.filter(i => isEligibleForYarboCare(i.product) && !i.protectionPlanId);
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
  const [selection, setSelection] = useState<Record<string, YarboCarePlanId>>({});
  const visible = eligibleWithoutProtection.filter(i => !dismissed[i.product.id]);
  if (visible.length === 0) return null;
  return <div className="bg-white rounded-xl border-2 border-yarbo-yellow/60 overflow-hidden" data-id="element-288">
      <div className="bg-yarbo-yellow/10 px-5 py-4 border-b border-yarbo-yellow/30 flex items-start gap-3" data-id="element-289">
        <div className="w-8 h-8 rounded-full bg-yarbo-yellow flex items-center justify-center flex-shrink-0" data-id="element-290">
          <ShieldCheck className="w-4 h-4 text-yarbo-dark" data-id="element-291" />
        </div>
        <div className="flex-1" data-id="element-292">
          <h3 className="font-extrabold text-yarbo-dark" data-id="element-293">
            Protect Your Yarbo System
          </h3>
          <p className="text-sm text-gray-700 mt-0.5" data-id="element-294">
            Add optional YarboCare protection before checkout. YarboCare can
            extend eligible Yarbo systems with additional warranty coverage and
            optional accidental damage protection.
          </p>
        </div>
      </div>

      <div className="divide-y divide-gray-100" data-id="element-295">
        {visible.map(({
        product
      }) => {
        const chosen = selection[product.id];
        return <div key={product.id} className="p-5 flex flex-col sm:flex-row gap-4" data-id="element-296">
              {/* Product summary */}
              <div className="flex items-center gap-3 sm:w-56 sm:flex-shrink-0" data-id="element-297">
                <div className="w-14 h-14 bg-gray-50 rounded-lg p-2 flex-shrink-0 border border-gray-200" data-id="element-298">
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-299" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-300">
                  <div className="text-sm font-bold text-yarbo-dark line-clamp-2 leading-tight" data-id="element-301">
                    {product.name}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-yarbo-dark/60 mt-1" data-id="element-302">
                    Eligible for YarboCare
                  </div>
                </div>
              </div>

              {/* Plan selector */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-303">
                {YARBOCARE_PLANS.map(plan => {
              const isChosen = chosen === plan.id;
              return <button key={plan.id} type="button" onClick={() => setSelection(s => ({
                ...s,
                [product.id]: plan.id
              }))} className={`text-left text-xs p-3 rounded-lg border-2 transition-all flex flex-col ${isChosen ? 'border-yarbo-dark bg-yarbo-yellow/5' : 'border-gray-200 bg-white hover:border-gray-400'}`} data-id="element-304">
                      <span className="font-bold text-yarbo-dark leading-tight line-clamp-2 mb-1" data-id="element-305">
                        {plan.title}
                      </span>
                      <span className="text-yarbo-dark font-extrabold mt-auto" data-id="element-306">
                        +${plan.priceMockup}
                      </span>
                    </button>;
            })}
              </div>

              {/* Actions */}
              <div className="flex sm:flex-col gap-2 sm:w-40 sm:flex-shrink-0" data-id="element-307">
                <button onClick={() => chosen && attachProtection(product.id, chosen)} disabled={!chosen} className={`flex-1 inline-flex items-center justify-center font-bold text-sm py-2.5 px-3 rounded-lg transition-colors ${chosen ? 'bg-yarbo-dark text-white hover:bg-black' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-308">
                  Add Protection
                </button>
                <button onClick={() => setDismissed(d => ({
              ...d,
              [product.id]: true
            }))} className="text-xs font-semibold text-gray-500 hover:text-yarbo-dark inline-flex items-center justify-center gap-1" data-id="element-309">
                  <X className="w-3 h-3" data-id="element-310" /> No Thanks
                </button>
              </div>
            </div>;
      })}
      </div>

      <div className="px-5 py-3 bg-gray-50 text-[11px] text-gray-500 flex items-start gap-2 leading-relaxed border-t border-gray-100" data-id="element-311">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-312" />
        <span data-id="element-313">{YARBOCARE_DISCLAIMER}</span>
      </div>
    </div>;
}